import React, { Fragment } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { Avatar, Button, Divider, LinearProgress, Skeleton, TextField, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { ArrowBack, ArrowForward, Person, Save,AutoAwesome } from "@mui/icons-material";
import { format, parseISO } from 'date-fns';

import '../styles/Views.css';
import MultiData from "../components/MultiData";
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentOrder } from "../state/selectors/currentOrder";
import { editMeasurement } from "../state/actions/measurement";
import { editSample} from "../state/actions/sample";
import { updateMeasurements } from "../state/handlers/measurements";
import { sampleLocators, updateSamples } from "../state/handlers/samples";

const MeasurementCard = ({measurement}) => {

    const dispatch = useDispatch();

    const handleChange = (event) =>{
        dispatch(editMeasurement(measurement.id, event.target.value));
    }

    return(
        <div className="column-container" style={{marginRight:'1em'}}>
            <Box fontWeight="medium">
                <Typography>{measurement.measurement_type.name}</Typography>
            </Box>
            <MultiData {...measurement.measurement_type} value={measurement.value} handleChange={handleChange}/>
        </div>
    )
}

const SampleCard = ({index,sample}) => {

    const dispatch = useDispatch();

    const handleChange = (event) =>{
        dispatch(editSample(sample.id, event.target.value));
    }

    return(
        <Fragment>
            <div className="row-container align-center">
                <Typography variant="h3">{index+1}</Typography>
                <div className="column-container margin">
                    <Box fontWeight="medium">
                        <Typography>Tipo de muestra</Typography>
                    </Box>
                    <TextField variant="outlined" value={sample.sampleType.name} size="small"/>
                </div>
                <div className="column-container margin">
                    <Box fontWeight="medium">
                        <Typography>Localizador</Typography>
                    </Box>
                    <TextField 
                        variant="outlined" 
                        value={sample.locator || ''} 
                        size="small"
                        onChange={handleChange}
                        
                    />
                </div>
                <div className="row-container margin">
                    {
                        sample.measurements.length > 0 && sample.measurements.map(measurement => <MeasurementCard key={measurement.id} measurement={measurement}/>)
                    }
                </div>
            </div>
            <Divider style={{marginTop:'1em'}}/>
        </Fragment>
    )
};

const Sample = () => {

    const {id} = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const {order,patient,samples, measurements, status} = useSelector(selectCurrentOrder);
    const measurementsWithoutSamples = measurements.filter(m=>!m.sample_id);

    const handleSave = () => {
        dispatch(updateSamples(1,order.id, samples));
        dispatch(updateMeasurements(1, order.id, measurements));
    }

    const handleLocators = () => {
        dispatch(sampleLocators(1,samples));
    };

    const handleContinue = () =>{
        navigate('/order');
    }

    return(
        <Fragment>
            <div className="column-container" style={{height:'90vh'}} >
                <div className="header row-container" style={{paddingTop:'1em'}}>
                    <div className="column-container " style={{paddingRight:'1em',width:'15em'}}> 
                        <Box fontWeight="bold">
                            <Typography variant="h6" color="text.black">{`Orden N° ${order.number}`}</Typography>
                        </Box>
                        <Box fontWeight={750}>
                            <Typography color="text.gray" variant="body2">{format(parseISO(order?.date), 'dd/MM/yyyy')}</Typography>
                        </Box>
                    </div>
                    <Divider orientation="vertical"  sx={{ height:"5.5em"}} />
                    <div className="row-container" style={{marginLeft:'2em'}}>
                        <div className="column-container justify-center" style={{marginRight:'1em'}}>
                            <Avatar style={{height:'3em', width:'3em'}}>
                                <Person fontSize="large"/>
                            </Avatar>
                        </div>
                        <div className="column-container " style={{paddingRight:'1em',width:'20em'}}> 
                            {
                                !patient &&
                                <Fragment>
                                    <Skeleton/>
                                    <Skeleton/>
                                    <Skeleton/>
                                    <Skeleton/>
                                </Fragment>
                            }
                            {   
                                patient &&                        
                                <Fragment>
                                    <Box fontWeight="medium">
                                        <Typography variant="h6" color="text.black">{`${patient.first_name} ${patient.last_name}`}</Typography>
                                    </Box>
                                    <Typography color="text.gray" variant="body2">{`N° ${patient.number}`}</Typography>
                                    <Typography color="text.gray" variant="body2">{`Fecha Nac. ${format(parseISO(patient.birth_day), 'dd/MM/yyyy')}`}</Typography>
                                    <Typography color="text.gray" variant="body2">{patient.gender}</Typography>
                                </Fragment>
                            }                    
                        </div>
                    </div>
                    <div className="grow"></div>
                    <div className="column-container align-right">
                        <Link to={`/order/${order.id}`} style={{textDecoration:'none'}}>
                            <Button 
                                color="primary"
                                startIcon={<ArrowBack color="secondary" />}
                                sx={{height:'2.5em', textTransform:'none', paddingLeft:'2em', paddingRight:'2em',  width:'15em', marginBottom:'1em'}}>
                                    <Box fontWeight="medium">
                                        <Typography>Volver a la orden</Typography>
                                    </Box>
                            </Button>
                        </Link>
                    </div>
                </div>
                <div className="body grow" style={{overflowY:'scroll'}}>
                    {
                        measurementsWithoutSamples.length > 0 &&
                        <div style={{marginBottom:'2em'}}>
                            <Typography variant="h6">Mediciones o datos requeridos:</Typography>
                            <div className="row-container margin">
                                {measurementsWithoutSamples.map(measurement => <MeasurementCard key={measurement.id} measurement={measurement}/>)}
                            </div>
                        </div>
                    }
                    {
                        <div>
                            <Typography variant="h6">Muestras:</Typography>
                            {samples.map((sample,index)=><SampleCard key={sample.id} index={index} sample={sample}/>)}
                        </div>
                    }
                </div>

                    {
                        status.loadingSamples || status.loadingMeasurements && 
                        <LinearProgress/>
                    }

                <div className="header row-container grow" style={{minHeight:'4em'}}>

                    <div className="margin">
                        <Button 
                            variant="contained"
                            color="secondary"
                            endIcon={<AutoAwesome/>}
                            onClick={()=>handleLocators()}
                            disabled={status.savedSamples}
                            sx={{height:'2.5em', textTransform:'none', paddingLeft:'1em', paddingRight:'1em', width:'15em', marginBottom:'1em'}}
                            
                        >
                            <Box fontWeight="medium">
                                <Typography>Automatico</Typography>
                            </Box>
                        </Button>
                    </div>
                    <div className="grow"></div>
                    <div className="margin">
                        <Button 
                            variant="contained"
                            color="secondary"
                            endIcon={<Save/>}
                            onClick={()=>handleSave()}
                            sx={{height:'2.5em', textTransform:'none', paddingLeft:'1em', paddingRight:'1em', width:'15em', marginBottom:'1em'}}
                            disabled={status.savedSamples && status.savedMeasurements}
                        >
                            <Box fontWeight="medium">
                                <Typography>Guardar</Typography>
                            </Box>
                        </Button>
                    </div> 
                    <div className="margin">
                    <Button 
                        color="primary"
                        endIcon={<ArrowForward color="secondary" />}
                        onClick={()=>handleContinue()}
                        disabled={!status.savedSamples && !status.savedMeasurements}
                        sx={{height:'2.5em', textTransform:'none', paddingLeft:'2em', paddingRight:'2em',  width:'15em'}}>
                            <Box fontWeight="medium">
                                <Typography>Continuar</Typography>
                            </Box>
                    </Button>
                    </div> 
                </div>
            </div>
        </Fragment>
    );
};

export default Sample;